/**
 * Product Approvals Management - Redesigned with EntityManagement design system
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import InboxIcon from '@mui/icons-material/Inbox';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SellIcon from '@mui/icons-material/Sell';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CategoryIcon from '@mui/icons-material/Category';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSnackbar } from 'notistack';
import DOMPurify from 'dompurify';
import * as productApprovalService from '../services/productApprovalService';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import { ProductFieldsView } from '../components/product/EditRequestDiffComponents';
import AssigneeCell from '../components/common/AssigneeCell';

import './EntityManagement.css';
import './ProductDetailsModal.css';

type StatusFilter = '' | 'pending_review' | 'active' | 'rejected' | 'inactive' | 'out_of_stock' | 'all';

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'همه وضعیت‌ها' },
  { value: 'pending_review', label: 'در انتظار بررسی' },
  { value: 'active', label: 'تایید شده' },
  { value: 'rejected', label: 'رد شده' },
  { value: 'inactive', label: 'غیرفعال' },
  { value: 'out_of_stock', label: 'ناموجود' }
];

const getStatusLabel = (status: string, isVerified?: boolean): string => {
  const s = status?.toLowerCase();
  switch (s) {
    case 'active':
      return isVerified === true ? 'تایید شده' : isVerified === false ? 'در انتظار تایید' : 'فعال';
    case 'pending':
    case 'pending_review':
    case 'pending_verification':
      return 'در انتظار بررسی';
    case 'rejected':
      return 'رد شده';
    case 'inactive':
      return 'غیرفعال';
    case 'out_of_stock':
      return 'ناموجود';
    default:
      return status || 'نامشخص';
  }
};

const getStatusTagClass = (status: string, isVerified?: boolean): string => {
  const s = status?.toLowerCase();
  if (s === 'active' && isVerified === true) return 'tag tag-sm tag-success';
  if (s === 'rejected') return 'tag tag-sm tag-danger';
  if (s === 'pending' || s === 'pending_review' || s === 'pending_verification') return 'tag tag-sm tag-warning';
  if (s === 'active' && isVerified === false) return 'tag tag-sm tag-warning';
  return 'tag tag-sm tag-inactive';
};

const ProductApprovalsManagement: React.FC = () => {
  const [approvals, setApprovals] = useState<productApprovalService.IProductApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('pending_review');

  // Stats
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [currentProduct, setCurrentProduct] = useState<productApprovalService.IProductApproval | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [operationSuccess, setOperationSuccess] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const filterValue = filterStatus || 'all';
      const result = await productApprovalService.getProductsWithFilter(filterValue, page + 1, rowsPerPage);
      let filtered = result.approvals;
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        filtered = result.approvals.filter((a: productApprovalService.IProductApproval) =>
          a.name.toLowerCase().includes(q) ||
          a.storeId?.name?.toLowerCase().includes(q) ||
          a.sku?.toLowerCase().includes(q)
        );
      }
      setApprovals(filtered);
      setTotalCount(result.pagination.total);
    } catch (error) {
      console.error('Error loading approvals:', error);
      enqueueSnackbar('خطا در دریافت محصولات', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterStatus, searchTerm, enqueueSnackbar]);

  const fetchStats = useCallback(async () => {
    try {
      const [all, pending, approved, rejected] = await Promise.all([
        productApprovalService.getProductsWithFilter('all', 1, 1),
        productApprovalService.getProductsWithFilter('pending_review', 1, 1),
        productApprovalService.getProductsWithFilter('active', 1, 1),
        productApprovalService.getProductsWithFilter('rejected', 1, 1)
      ]);
      setStats({
        total: all.pagination?.total || 0,
        pending: pending.pagination?.total || 0,
        approved: approved.pagination?.total || 0,
        rejected: rejected.pagination?.total || 0
      });
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchApprovals(); }, [fetchApprovals]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const formatPrice = (price?: number): string => {
    if (price === undefined || price === null) return 'بدون قیمت';
    return `${price.toLocaleString('fa-IR')} تومان`;
  };

  const formatDate = (d?: string): string => {
    if (!d) return 'نامشخص';
    try { return new Date(d).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return 'نامعتبر'; }
  };

  const getAdminName = (product: any, type: 'approved' | 'rejected'): string => {
    if (!product) return 'نامشخص';
    if (product.verificationInfo) {
      if (type === 'approved' && product.verificationInfo.approvedBy?.name) return product.verificationInfo.approvedBy.name;
      if (type === 'rejected' && product.verificationInfo.rejectedBy?.name) return product.verificationInfo.rejectedBy.name;
    }
    if (type === 'approved' && product.verifiedBy?.name) return product.verifiedBy.name;
    return 'نامشخص';
  };

  const getVerificationDate = (product: any, type: 'approved' | 'rejected'): string => {
    if (!product) return 'نامشخص';
    if (product.verificationInfo) {
      if (type === 'approved' && product.verificationInfo.approvedAt) return formatDate(product.verificationInfo.approvedAt);
      if (type === 'rejected' && product.verificationInfo.rejectedAt) return formatDate(product.verificationInfo.rejectedAt);
    }
    if (type === 'approved' && product.verifiedAt) return formatDate(product.verifiedAt);
    return 'نامشخص';
  };

  const handleSearch = () => { setSearchTerm(searchInput); setPage(0); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };
  const clearFilters = () => { setSearchTerm(''); setSearchInput(''); setFilterStatus(''); setPage(0); };

  const openDetails = (p: productApprovalService.IProductApproval) => { setCurrentProduct(p); setGalleryIdx(0); setDetailsOpen(true); };
  const openApprove = (p: productApprovalService.IProductApproval) => { setCurrentProduct(p); setApprovalNote(''); setOperationSuccess(null); setApproveOpen(true); };
  const openReject = (p: productApprovalService.IProductApproval) => { setCurrentProduct(p); setRejectionReason(''); setOperationSuccess(null); setRejectOpen(true); };
  const closeAll = () => { setDetailsOpen(false); setApproveOpen(false); setRejectOpen(false); setCurrentProduct(null); };

  const handleApprove = async () => {
    if (!currentProduct) return;
    setIsApproving(true);
    try {
      await productApprovalService.approveProduct(currentProduct._id, approvalNote);
      setOperationSuccess(`محصول "${currentProduct.name}" با موفقیت تایید شد`);
      enqueueSnackbar('محصول با موفقیت تایید شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchApprovals(); fetchStats(); }, 1000);
    } catch (error) {
      console.error('Error approving product:', error);
      enqueueSnackbar('خطا در تایید محصول', { variant: 'error' });
    } finally { setIsApproving(false); }
  };

  const handleReject = async () => {
    if (!currentProduct || !rejectionReason.trim()) {
      enqueueSnackbar('دلیل رد محصول الزامی است', { variant: 'warning' });
      return;
    }
    setIsRejecting(true);
    try {
      await productApprovalService.rejectProduct(currentProduct._id, rejectionReason);
      setOperationSuccess(`محصول "${currentProduct.name}" رد شد`);
      enqueueSnackbar('محصول با موفقیت رد شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchApprovals(); fetchStats(); }, 1000);
    } catch (error) {
      console.error('Error rejecting product:', error);
      enqueueSnackbar('خطا در رد محصول', { variant: 'error' });
    } finally { setIsRejecting(false); }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalCount);
  const hasActiveFilters = !!searchTerm || !!filterStatus;

  return (
    <div className="entity-page">
      <MyShiftStatusBanner category="product_approval" />
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">تأیید محصولات</h1>
          <p className="entity-subtitle">بررسی و تأیید محصولات ارسالی فروشندگان</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={() => { fetchApprovals(); fetchStats(); }} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><InventoryIcon /></div>
          <div>
            <p className="entity-stat-label">کل محصولات</p>
            <p className="entity-stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><HourglassEmptyIcon /></div>
          <div>
            <p className="entity-stat-label">در انتظار بررسی</p>
            <p className="entity-stat-value">{stats.pending}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">تایید شده</p>
            <p className="entity-stat-value">{stats.approved}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">رد شده</p>
            <p className="entity-stat-value">{stats.rejected}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی نام محصول، فروشگاه یا کد..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as StatusFilter); setPage(0); }}
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {filterStatus && (
              <span className="filter-tag">
                وضعیت: {statusOptions.find(o => o.value === filterStatus)?.label}
                <button onClick={() => setFilterStatus('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && !approvals.length ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : approvals.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><InboxIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">محصولی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>محصول</th>
                  <th>فروشگاه</th>
                  <th>قیمت</th>
                  <th>موجودی</th>
                  <th>وضعیت</th>
                  <th>مسئول</th>
                  <th>تاریخ</th>
                  <th style={{ width: 160 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((product) => (
                  <tr key={product._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          {product.thumbnail ? (
                            <img src={product.thumbnail} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                          ) : (
                            <InventoryIcon />
                          )}
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{product.name}</span>
                          {product.sku && <span className="entity-desc">کد: {product.sku}</span>}
                        </div>
                      </div>
                    </td>
                    <td><span className="entity-desc">{product.storeId?.name || 'نامشخص'}</span></td>
                    <td><span className="entity-desc">{formatPrice(product.price)}</span></td>
                    <td><span className="entity-desc">{product.stock ?? '-'}</span></td>
                    <td><span className={getStatusTagClass(product.status, product.isVerified)}>{getStatusLabel(product.status, product.isVerified)}</span></td>
                    <td><AssigneeCell value={(product as any).assignedTo} /></td>
                    <td><span className="entity-desc">{formatDate(product.createdAt)}</span></td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="جزئیات" arrow>
                          <button className="action-btn edit" onClick={() => openDetails(product)}>
                            <VisibilityIcon />
                          </button>
                        </Tooltip>
                        {product.isVerified !== true && (
                          <>
                            <Tooltip title="تایید" arrow>
                              <button className="action-btn toggle active" onClick={() => openApprove(product)}>
                                <CheckCircleIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title="رد" arrow>
                              <button className="action-btn delete" onClick={() => openReject(product)}>
                                <CancelIcon />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {approvals.map((product) => (
                <div key={product._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      {product.thumbnail ? (
                        <img src={product.thumbnail} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                      ) : (
                        <InventoryIcon />
                      )}
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{product.name}</div>
                      <div className="entity-card-desc">{product.storeId?.name || 'نامشخص'} - {formatPrice(product.price)}</div>
                    </div>
                    <div className="entity-card-status">
                      <span className={getStatusTagClass(product.status, product.isVerified)}>{getStatusLabel(product.status, product.isVerified)}</span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => openDetails(product)}>
                      <VisibilityIcon fontSize="small" /> جزئیات
                    </button>
                    {product.isVerified !== true && (
                      <>
                        <button className="entity-card-btn" onClick={() => openApprove(product)}>
                          <CheckCircleIcon fontSize="small" /> تایید
                        </button>
                        <button className="entity-card-btn danger" onClick={() => openReject(product)}>
                          <CancelIcon fontSize="small" /> رد
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalCount}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page + 1} / {totalPages || 1}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 0}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog — Redesigned */}
      <Dialog open={detailsOpen} onClose={closeAll} maxWidth="md" fullWidth className="productapproval-details-dialog pa-details-dialog">
        {currentProduct && (() => {
          const p = currentProduct;
          const images = p.images && p.images.length > 0 ? p.images : (p.thumbnail ? [p.thumbnail] : []);
          const hasImages = images.length > 0;
          const featured = hasImages ? images[Math.min(galleryIdx, images.length - 1)] : null;
          const hasDiscount = p.discountPrice !== undefined && p.discountPrice !== null && p.discountPrice < p.price;
          const decided = p.isVerified === true || p.status?.toLowerCase() === 'rejected';
          return (
            <>
              <div className="pa-modal-titlebar">
                <span><InventoryIcon fontSize="small" /> جزئیات محصول</span>
                <button className="pa-close" onClick={closeAll} aria-label="بستن">
                  <CloseIcon fontSize="small" />
                </button>
              </div>

              <DialogContent className="pa-details-content">
                {/* Hero */}
                <div className="pa-hero">
                  <div
                    className={`pa-hero-media ${hasImages ? 'clickable' : ''}`}
                    onClick={() => featured && (setPreviewImage(featured), setImagePreviewOpen(true))}
                  >
                    {featured ? (
                      <>
                        <img src={featured} alt={p.name} />
                        <span className="pa-hero-zoom"><ZoomInIcon fontSize="small" /></span>
                      </>
                    ) : (
                      <div className="pa-hero-noimg">
                        <NoPhotographyIcon />
                        <span>بدون تصویر</span>
                      </div>
                    )}
                  </div>

                  <div className="pa-hero-info">
                    <div className="pa-hero-badges">
                      <span className={getStatusTagClass(p.status, p.isVerified)}>
                        {getStatusLabel(p.status, p.isVerified)}
                      </span>
                      {p.isDeleted && <span className="tag tag-sm tag-danger">حذف‌شده</span>}
                    </div>

                    <h2 className="pa-hero-name">{p.name || 'بدون نام'}</h2>

                    <div className="pa-hero-store">
                      {p.storeId?.logo ? (
                        <img src={p.storeId.logo} alt={p.storeId?.name} className="pa-store-logo" />
                      ) : (
                        <span className="pa-store-logo placeholder"><StorefrontIcon fontSize="small" /></span>
                      )}
                      <span className="pa-store-name">{p.storeId?.name || 'فروشگاه نامشخص'}</span>
                    </div>

                    <div className="pa-hero-price">
                      {hasDiscount ? (
                        <>
                          <span className="pa-price-final">{formatPrice(p.discountPrice)}</span>
                          <span className="pa-price-old">{formatPrice(p.price)}</span>
                          <span className="pa-price-off">
                            {Math.round((1 - (p.discountPrice as number) / p.price) * 100)}٪ تخفیف
                          </span>
                        </>
                      ) : (
                        <span className="pa-price-final">{formatPrice(p.price)}</span>
                      )}
                    </div>

                    <div className="pa-hero-chips">
                      {p.accountTypeId?.name && (
                        <span className="pa-chip"><CategoryIcon fontSize="inherit" /> {p.accountTypeId.name}</span>
                      )}
                      {p.sku && (
                        <span className="pa-chip"><LocalOfferIcon fontSize="inherit" /> {p.sku}</span>
                      )}
                      <span className="pa-chip"><CalendarTodayIcon fontSize="inherit" /> {formatDate(p.createdAt)}</span>
                      {p.slug && (
                        <a className="pa-chip link" href={`/products/${p.slug}`} target="_blank" rel="noreferrer">
                          <OpenInNewIcon fontSize="inherit" /> مشاهده در سایت
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick stat tiles */}
                <div className="pa-stats">
                  <div className="pa-stat">
                    <span className="pa-stat-icon stock"><Inventory2Icon fontSize="small" /></span>
                    <div>
                      <div className="pa-stat-value">{(p.stock ?? 0).toLocaleString('fa-IR')}</div>
                      <div className="pa-stat-label">موجودی</div>
                    </div>
                  </div>
                  <div className="pa-stat">
                    <span className="pa-stat-icon sales"><SellIcon fontSize="small" /></span>
                    <div>
                      <div className="pa-stat-value">{(p.sales ?? 0).toLocaleString('fa-IR')}</div>
                      <div className="pa-stat-label">فروش</div>
                    </div>
                  </div>
                  <div className="pa-stat">
                    <span className="pa-stat-icon views"><VisibilityIcon fontSize="small" /></span>
                    <div>
                      <div className="pa-stat-value">{(p.views ?? 0).toLocaleString('fa-IR')}</div>
                      <div className="pa-stat-label">بازدید</div>
                    </div>
                  </div>
                  <div className="pa-stat">
                    <span className="pa-stat-icon id"><FingerprintIcon fontSize="small" /></span>
                    <div>
                      <div className="pa-stat-value mono">{p._id.slice(-8)}</div>
                      <div className="pa-stat-label">شناسه</div>
                    </div>
                  </div>
                </div>

                {/* Gallery */}
                {hasImages && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">تصاویر محصول <span className="pa-count">{images.length}</span></h3>
                    <div className="pa-gallery">
                      <div
                        className="pa-gallery-main"
                        onClick={() => featured && (setPreviewImage(featured), setImagePreviewOpen(true))}
                      >
                        <img src={featured as string} alt={p.name} />
                        <span className="pa-hero-zoom"><ZoomInIcon fontSize="small" /></span>
                      </div>
                      {images.length > 1 && (
                        <div className="pa-gallery-thumbs">
                          {images.map((img, idx) => (
                            <button
                              key={idx}
                              className={`pa-thumb ${idx === galleryIdx ? 'active' : ''}`}
                              onClick={() => setGalleryIdx(idx)}
                            >
                              <img src={img} alt={`تصویر ${idx + 1}`} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Description */}
                {p.description && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">توضیحات</h3>
                    {/* توضیحات HTML غنی است؛ با DOMPurify پاکسازی و امن رندر می‌شود. */}
                    <div
                      className="rich-text-view"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.description) }}
                    />
                  </section>
                )}

                {/* Specialized AccountType fields */}
                {(p as any).fields && Object.keys((p as any).fields).length > 0 && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">
                      ویژگی‌های تخصصی
                      {p.accountTypeId?.name && <span className="pa-section-sub">— {p.accountTypeId.name}</span>}
                    </h3>
                    <ProductFieldsView
                      fieldsData={(p as any).fields}
                      accountTypeFields={(p.accountTypeId as any)?.fields || []}
                    />
                  </section>
                )}

                {/* Seller */}
                <section className="pa-section">
                  <h3 className="pa-section-title">اطلاعات فروشنده</h3>
                  <div className="pa-seller">
                    {p.storeId?.logo ? (
                      <img src={p.storeId.logo} alt={p.storeId?.name} className="pa-seller-logo" />
                    ) : (
                      <span className="pa-seller-logo placeholder"><StorefrontIcon /></span>
                    )}
                    <div className="pa-seller-info">
                      <div className="pa-seller-name">{p.storeId?.name || 'نامشخص'}</div>
                      <div className="pa-seller-meta">
                        <span>شناسه: {p.storeId?._id?.slice(-8) || 'نامشخص'}</span>
                        {p.storeId?.slug && <span>نامک: {p.storeId.slug}</span>}
                      </div>
                    </div>
                    {p.storeId?.slug && (
                      <a className="pa-seller-link" href={`/store/${p.storeId.slug}`} target="_blank" rel="noreferrer">
                        <OpenInNewIcon fontSize="small" /> فروشگاه
                      </a>
                    )}
                  </div>
                </section>

                {/* Status & history */}
                {decided && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">وضعیت و تاریخچه</h3>
                    {p.isVerified === true && (
                      <div className={`pa-verdict approved`}>
                        <div className="pa-verdict-head">
                          <CheckCircleIcon fontSize="small" /> تایید شده
                        </div>
                        <div className="pa-verdict-grid">
                          <div><span className="pa-vk">تایید توسط</span><span className="pa-vv">{getAdminName(p, 'approved')}</span></div>
                          <div><span className="pa-vk">تاریخ تایید</span><span className="pa-vv">{getVerificationDate(p, 'approved')}</span></div>
                        </div>
                        {p.verificationInfo?.approvalNote && (
                          <div className="pa-verdict-note">
                            <span className="pa-vk">یادداشت</span>
                            <p>{p.verificationInfo.approvalNote}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {p.isVerified === false && p.status?.toLowerCase() === 'rejected' && (
                      <div className={`pa-verdict rejected`}>
                        <div className="pa-verdict-head">
                          <CancelIcon fontSize="small" /> رد شده
                        </div>
                        <div className="pa-verdict-grid">
                          <div><span className="pa-vk">رد توسط</span><span className="pa-vv">{getAdminName(p, 'rejected')}</span></div>
                          <div><span className="pa-vk">تاریخ رد</span><span className="pa-vv">{getVerificationDate(p, 'rejected')}</span></div>
                        </div>
                        {p.verificationInfo?.rejectionReason && (
                          <div className="pa-verdict-note">
                            <span className="pa-vk">دلیل رد</span>
                            <p>{p.verificationInfo.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                )}
              </DialogContent>

              <div className="pa-details-actions">
                <button className="pa-btn ghost" onClick={closeAll}>بستن</button>
                {p.isVerified !== true && (
                  <>
                    <button className="pa-btn reject" onClick={() => { setDetailsOpen(false); openReject(p); }}>
                      <CancelIcon fontSize="small" /> رد محصول
                    </button>
                    <button className="pa-btn approve" onClick={() => { setDetailsOpen(false); openApprove(p); }}>
                      <CheckCircleIcon fontSize="small" /> تایید محصول
                    </button>
                  </>
                )}
              </div>
            </>
          );
        })()}
      </Dialog>

      {/* Approve Confirm Dialog */}
      <Dialog open={approveOpen} onClose={closeAll} maxWidth="xs" fullWidth className="productapproval-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon" style={{ background: 'var(--color-success-bg)' }}>
            <CheckCircleIcon style={{ fontSize: 28, color: 'var(--color-success-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">تایید محصول</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-success-dark)' }}>
              <CheckCircleIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <>
              <p className="delete-dialog-text">
                آیا از تایید محصول <strong>«{currentProduct?.name}»</strong> اطمینان دارید؟
                <br />با تایید، محصول در فروشگاه قابل مشاهده خواهد بود.
              </p>
              <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
                <textarea
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  placeholder="یادداشت (اختیاری) - برای فروشنده نمایش داده می‌شود..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: 'var(--color-bg-paper)',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>
            </>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={isApproving}>انصراف</button>
            <button
              className="btn btn-danger"
              style={{ background: 'var(--color-success-dark)' }}
              onClick={handleApprove}
              disabled={isApproving || !!operationSuccess}
            >
              {isApproving && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CheckCircleIcon fontSize="small" /> تایید
            </button>
          </div>
        </div>
      </Dialog>

      {/* Reject Confirm Dialog */}
      <Dialog open={rejectOpen} onClose={closeAll} maxWidth="xs" fullWidth className="productapproval-confirm-dialog">
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon">
            <CancelIcon style={{ fontSize: 28, color: 'var(--color-error-dark)' }} />
          </div>
          <h3 className="delete-dialog-title">رد محصول</h3>
          {operationSuccess ? (
            <p className="delete-dialog-text" style={{ color: 'var(--color-error-dark)' }}>
              <CancelIcon style={{ fontSize: 18, verticalAlign: 'middle', marginLeft: 4 }} />
              {operationSuccess}
            </p>
          ) : (
            <>
              <p className="delete-dialog-text">
                دلیل رد محصول <strong>«{currentProduct?.name}»</strong> را وارد کنید.
                <br />این دلیل به فروشنده نمایش داده می‌شود.
              </p>
              <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="دلیل رد را وارد کنید..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${!rejectionReason.trim() ? 'var(--color-error-dark)' : 'var(--color-border)'}`,
                    borderRadius: 8,
                    fontFamily: 'inherit',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    background: 'var(--color-bg-paper)',
                    color: 'var(--color-text-primary)'
                  }}
                />
                {!rejectionReason.trim() && (
                  <span style={{ color: 'var(--color-error-dark)', fontSize: 12, marginTop: 4, display: 'block' }}>
                    وارد کردن دلیل الزامی است
                  </span>
                )}
              </div>
            </>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={isRejecting}>انصراف</button>
            <button
              className="btn btn-danger"
              onClick={handleReject}
              disabled={isRejecting || !rejectionReason.trim() || !!operationSuccess}
            >
              {isRejecting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <CancelIcon fontSize="small" /> رد محصول
            </button>
          </div>
        </div>
      </Dialog>

      {/* Image Preview Overlay */}
      {imagePreviewOpen && previewImage && (
        <div
          onClick={() => setImagePreviewOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <button
            onClick={() => setImagePreviewOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon />
          </button>
          <img
            src={previewImage}
            alt="پیش‌نمایش"
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProductApprovalsManagement;
